import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, computed, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { L10N_LOCALE } from 'angular-l10n';
import { BehaviorSubject, Subscription, debounceTime } from 'rxjs';
import { PagedTenantResponse, Tenant, TenantApi } from 'src/app/hickory-api';
import { ApiError } from '../../model/utility';

class TreeNode {
  level: number;
  key: string;
  name: string;
  expandable: boolean;
  children: TreeNode[];

  source?: Tenant;
  count?: number;
  checked?: boolean;
  selected?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;

  get parentId(): string | undefined {
    return this.source?.brandName;
  }

  constructor(key: string, source?: Tenant) {
    this.source = source;
    this.key = key;
    this.name = source?.name ?? key;
    this.level = 1;
    this.children = [];
    this.expandable = false;
  }

  addChild(node: TreeNode) {
    this.level = 0;
    this.expandable = true;
    this.children.push(node);
    this.count = this.children.length;
  }
}

@Component({
  selector: 'app-tenant-selection',
  templateUrl: './tenant-selection.component.html',
  styleUrls: ['./tenant-selection.component.scss'],
  standalone: false,
})
export class TenantSelectionComponent implements OnInit, OnDestroy {
  public readonly locale = inject(L10N_LOCALE);
  private readonly tenantApi = inject(TenantApi);
  private readonly snackBar = inject(MatSnackBar);

  readonly selectionsIn = input<BehaviorSubject<Tenant[]>>();
  readonly selectionsOut = output<Tenant[]>();

  public readonly isError = signal(false); // indicates that we encountered an error fetching the tenants
  public readonly inProgress = signal(true); // indicates that we're fetching nodes

  // cleanup subscriptions on destroy
  subscription: Subscription = new Subscription();

  public readonly selectedItems = signal<TreeNode[]>([]);
  public readonly selectedTenants = signal<Tenant[]>([]);
  public readonly selectedTotal = computed(() => this.selectedTenants().length);

  // search
  public readonly searchControl = new UntypedFormControl();
  public readonly searchQuery = signal<string | null>(null);
  public readonly searchResults = signal<TreeNode[] | undefined>(undefined);
  public readonly searchInProgress = signal(false);

  // paste tenant IDs functionality - allows users to paste a list of tenant IDs for bulk selection
  pasteControl = new UntypedFormControl();

  // tree view
  treeControl = new FlatTreeControl<TreeNode>(
    (node) => node.level,
    (node) => node.expandable,
  );
  treeFlattener = new MatTreeFlattener(
    (node: TreeNode, level: number) => {
      node.level = level;
      return node;
    },
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children,
  );
  treeDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
  treeDataUpdates = new BehaviorSubject<void>(undefined);

  // data
  // key: Tenant.id OR brand name -- cache of all nodes
  private readonly dataMap = signal<Map<string, TreeNode>>(new Map<string, TreeNode>());
  private readonly brands = signal<string[]>([]);

  private readonly brandNodes = computed(() => {
    const nodes: TreeNode[] = [];
    this.brands().forEach((b) => {
      const node = this.dataMap().get(b);
      if (node) nodes.push(node);
    });

    return nodes;
  });

  productControl = new UntypedFormControl();
  productEditions = signal<string[]>([]);
  productFilter = signal<string | undefined>(undefined);

  ngOnInit(): void {
    this.fetchAll();

    // listen for updates to selections from the parent component
    if (this.selectionsIn())
      this.subscription.add(
        this.selectionsIn()!
          .asObservable()
          .subscribe((selections) => this.updateSelectionsFromInput(selections)),
      );

    // debounce group search and trigger the refresh
    this.subscription.add(
      this.searchControl.valueChanges.pipe(debounceTime(200)).subscribe((newValue) => this.onSearch(newValue)),
    );

    // listen for additional tree data changes
    this.subscription.add(
      this.treeDataUpdates
        .asObservable()
        .pipe(debounceTime(100))
        .subscribe((_) => this.updateTreeDataSource()),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  fetchAll(page: number = 1, pageSize: number = 200) {
    if (page == 1) this.resetData();
    this.inProgress.set(true);
    this.isError.set(false);
    this.subscription.add(
      this.tenantApi
        .getTenants(undefined, undefined, undefined, undefined, this.productFilter(), false, page, pageSize)
        .subscribe({
          next: (res) => this.processFetchResponse(res, undefined),
          error: (err: ApiError) => this.processFetchResponse(undefined, err),
        }),
    );
  }

  private processFetchResponse(res?: PagedTenantResponse, err?: ApiError) {
    if (err) {
      this.inProgress.set(false);
      this.isError.set(true);
      return;
    }

    if (!res) return;

    res?.items?.forEach((tenant) => this.addTenant(tenant));

    // sort tenants as children on brands
    this.brandNodes().forEach((r) => (r.children = [...r.children].sort((a, b) => a.name.localeCompare(b.name))));
    this.updateTreeDataSource();

    if (res?.pageCount && res?.page && res.pageCount > res.page) {
      this.fetchAll(res?.page + 1);
    } else {
      this.inProgress.set(false);
    }
  }

  private addTenant(tenant: Tenant): TreeNode {
    if (!!tenant.productEdition?.length && !this.productEditions().includes(tenant.productEdition ?? '')) {
      this.productEditions.set([...this.productEditions(), tenant.productEdition]);
      this.productEditions.set(this.productEditions().sort((a, b) => a.localeCompare(b)));
    }
    const tenantKey = '' + tenant.id;
    const tenantNode = this.dataMap().get(tenantKey) ?? new TreeNode(tenantKey, tenant);
    this.dataMap().set(tenantKey, tenantNode);

    const brandKey = tenant.brandName ?? 'Undetermined';
    const brandNode = this.dataMap().get(brandKey) ?? new TreeNode(brandKey);
    brandNode.addChild(tenantNode);
    if (!this.brands().includes(brandKey)) this.brands.set([...this.brands(), brandKey]);
    this.dataMap.update((map) => {
      map.set(brandNode.key, brandNode);
      return map;
    });
    return tenantNode;
  }

  onSearch(newValue: string) {
    const query = newValue || null;
    this.searchQuery.set(query);
    if (query) {
      const regex = new RegExp(query, 'i');
      this.searchResults.set(
        [...this.dataMap().values()]
          .filter((n) => !!n.source && regex.test(n.name))
          .sort((a, b) => a.name.localeCompare(b.name))
          .slice(0, 25),
      );
    } else {
      this.onResetSearch();
    }
  }

  onResetSearch() {
    this.searchControl.reset();
    this.searchQuery.set(null);
    this.searchResults.set(undefined);
  }

  onProductFilterChange(value: string) {
    this.productFilter.set(value);
    this.fetchAll();
  }

  onAutoComplete(node: TreeNode) {
    this.onSelectedChange(node, true);
  }

  onSelectedChange(node: TreeNode, selected: boolean) {
    node.checked = selected;
    const items = this.selectedItems().filter((n: TreeNode) => n.key !== node.key);
    if (selected) items.push(node);
    this.selectedItems.set(items);
    this.selectedTenants.set(this.getSelectedTenants());
    this.selectionsOut.emit(this.selectedTenants());
    this.treeDataUpdates.next();
  }

  onChipRemove(node: TreeNode) {
    this.onSelectedChange(node, false);
  }

  onPasteTenantIds() {
    const pastedValue = this.pasteControl.value;
    if (!pastedValue?.trim()) {
      return;
    }

    // Parse the pasted content - support comma, semicolon, newline, or space separation
    const rawIds = pastedValue
      .split(/[,;\n\r\s]+/)
      .map((id: string) => id.trim())
      .filter((id: string) => id.length > 0);

    const matchedCount = this.selectTenantsByIds(rawIds);
    const notFoundIds = rawIds.filter((id: string) => !this.dataMap().has(id));

    // Show toast message with results
    let message = `${matchedCount} tenant(s) selected`;
    if (notFoundIds.length > 0) {
      message += `. ${notFoundIds.length} ID(s) not found: ${notFoundIds.join(', ')}`;
    }

    const config = {
      duration: notFoundIds.length > 0 ? 5000 : 3000,
      panelClass: notFoundIds.length > 0 ? 'snackbar-warn-message' : 'snackbar-status-message',
    };

    this.snackBar.open(message, 'OK', config);

    // Clear the paste input after processing
    this.pasteControl.setValue('');
  }

  private selectTenantsByIds(tenantIds: string[]): number {
    let matchedCount = 0;

    tenantIds.forEach((id) => {
      const node = this.dataMap().get(id);
      if (node?.source && !this.isSelected(node.key)) {
        this.onSelectedChange(node, true);
        matchedCount++;
      }
    });

    return matchedCount;
  }

  private resetData() {
    this.brands.set([]);
    this.selectedItems.set([]);
    this.selectedTenants.set([]);
    this.dataMap.set(new Map<string, TreeNode>());
  }

  private updateTreeDataSource() {
    this.dataMap().forEach((n) => this.updateNodeState(n));
    const roots = this.brandNodes().sort((a, b) => a.name.localeCompare(b.name));
    roots.forEach((r) => (r.children = [...r.children].sort((a, b) => a.name.localeCompare(b.name))));
    this.treeDataSource.data = roots;
  }

  private updateSelectionsFromInput(selections: Tenant[]) {
    this.selectedItems.set(selections?.map?.((t) => this.addTenant(t)) ?? []);
    this.selectedTenants.set(this.getSelectedTenants());
    this.treeDataUpdates.next();
  }

  private getSelectedTenants(): Tenant[] {
    const nodes: TreeNode[] = [];
    const addDistinct = function (n: TreeNode) {
      if (!!n.source && !nodes.find((x) => n.key === x.key)) nodes.push(n);
      n.children?.forEach((c) => addDistinct(c));
    };

    this.selectedItems().forEach((n: TreeNode) => addDistinct(n));
    return nodes.flatMap((f) => (f.source ? [f.source] : []));
  }

  private updateNodeState(node: TreeNode) {
    node.checked = this.isChecked(node.key);
    node.selected = this.isSelected(node.key);
    node.indeterminate = this.isChildSelected(node.key);
    node.disabled = this.isDisabled(node.key);
  }

  private isSelected(id: string): boolean {
    if (!id) return false;
    return !!this.selectedItems().find((n: TreeNode) => n.key === id);
  }

  private isChecked(id: string): boolean {
    if (!id) return false;
    return this.isSelected(id) || this.isParentSelected(id);
  }

  private isParentSelected(id: string): boolean {
    if (!id) return false;
    const parentId = this.dataMap().get(id)?.parentId;
    if (!parentId) return false;
    if (id === parentId) return this.isSelected(id);
    return this.isSelected(parentId) || this.isParentSelected(parentId);
  }

  private isDisabled(id: string): boolean {
    return this.isParentSelected(id);
  }

  private isChildSelected(id: string): boolean {
    if (!id) return false;
    const item = this.dataMap().get(id);
    if (!item) return false;
    if (this.isChecked(id) || !item?.children?.length) {
      return false;
    } else {
      return !!item?.children?.find((n) => this.isSelected(n.key));
    }
  }
}
